import { useState, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import { format, isBefore, startOfDay, parseISO } from 'date-fns';
import { db } from '../../services/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { Calendar, AlertCircle } from 'lucide-react';
import 'react-day-picker/style.css';

export default function BookingDatePicker({ value, onChange, artistId }) {
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  // Fetch unavailable dates on mount
  useEffect(() => {
    const fetchUnavailableDates = async () => {
      if (!db) {
        setIsLoading(false);
        return;
      }

      try {
        const dates = new Set();

        // 1. Fetch blocked dates
        const blockedDatesRef = collection(db, 'blockedDates');
        const blockedSnapshot = await getDocs(blockedDatesRef);
        blockedSnapshot.docs.forEach((doc) => {
          const data = doc.data();
          if (data.date) {
            dates.add(data.date);
          }
        });

        // 2. Fetch confirmed bookings
        const bookingsQuery = query(
          collection(db, 'bookings'),
          where('status', '==', 'confirmed')
        );
        const bookingsSnapshot = await getDocs(bookingsQuery);
        bookingsSnapshot.docs.forEach((doc) => {
          const data = doc.data();
          // Only block if same artist or if artist is booked
          if (data.eventDate && (!artistId || data.artist === artistId)) {
            dates.add(data.eventDate);
          }
        });

        // 3. Fetch scheduled shows (not cancelled)
        const showsQuery = query(
          collection(db, 'shows'),
          where('status', '!=', 'cancelled')
        );
        const showsSnapshot = await getDocs(showsQuery);
        showsSnapshot.docs.forEach((doc) => {
          const data = doc.data();
          if (data.date) {
            // Handle different date formats
            const dateStr = typeof data.date === 'string' ? data.date : data.date?.toDate?.()?.toISOString?.()?.split('T')?.[0];
            if (dateStr) {
              dates.add(dateStr);
            }
          }
        });

        // Convert strings to Date objects
        const dateObjects = Array.from(dates).map((dateStr) => {
          try {
            return parseISO(dateStr);
          } catch {
            return null;
          }
        }).filter(Boolean);

        setUnavailableDates(dateObjects);
      } catch (error) {
        console.error('Error fetching unavailable dates:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUnavailableDates();
  }, [artistId]);

  const handleSelect = (date) => {
    if (date) {
      onChange(format(date, 'yyyy-MM-dd'));
      setIsOpen(false);
    }
  };

  const today = startOfDay(new Date());

  // Disable past dates and unavailable dates
  const isDateDisabled = (date) => {
    // Disable past dates
    if (isBefore(date, today)) {
      return true;
    }

    // Disable unavailable dates
    return unavailableDates.some(
      (unavailableDate) =>
        format(unavailableDate, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')
    );
  };

  const selectedDate = value ? parseISO(value) : undefined;

  return (
    <div className="relative">
      {/* Date Input Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-left text-white hover:border-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors"
      >
        <Calendar className="w-4 h-4 text-gray-400" />
        <span className={value ? 'text-white' : 'text-gray-500'}>
          {value
            ? format(parseISO(value), 'MMMM d, yyyy')
            : 'Select a date'}
        </span>
      </button>

      {/* Loading indicator */}
      {isLoading && (
        <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
          <span className="w-3 h-3 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
          Loading availability...
        </p>
      )}

      {/* Calendar Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Calendar */}
          <div className="absolute z-50 mt-2 p-4 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl">
            <style>{`
              .booking-calendar {
                --rdp-accent-color: #f97316;
                --rdp-accent-background-color: linear-gradient(to right, #f97316, #dc2626);
                --rdp-background-color: #111827;
                color: white;
              }
              .booking-calendar .rdp-month_caption {
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 0.5rem;
                position: relative;
              }
              .booking-calendar .rdp-caption_label {
                font-size: 1rem;
                font-weight: 600;
                color: white;
              }
              .booking-calendar .rdp-nav {
                display: flex;
                justify-content: space-between;
                align-items: center;
                width: 100%;
                position: absolute;
                left: 0;
                right: 0;
                padding: 0 4px;
                pointer-events: none;
              }
              .booking-calendar .rdp-button_previous,
              .booking-calendar .rdp-button_next {
                width: 28px;
                height: 28px;
                background: #374151;
                border-radius: 6px;
                display: flex;
                align-items: center;
                justify-content: center;
                color: white;
                cursor: pointer;
                border: none;
                pointer-events: auto;
                z-index: 10;
              }
              .booking-calendar .rdp-button_previous:hover,
              .booking-calendar .rdp-button_next:hover {
                background: #4b5563;
              }
              .booking-calendar .rdp-chevron {
                fill: white;
              }
              .booking-calendar .rdp-weekday {
                color: #9ca3af;
                font-size: 0.75rem;
                font-weight: normal;
                width: 36px;
                height: 36px;
              }
              .booking-calendar .rdp-day {
                width: 36px;
                height: 36px;
              }
              .booking-calendar .rdp-day_button {
                width: 36px;
                height: 36px;
                border-radius: 6px;
                font-size: 0.875rem;
                color: white;
                cursor: pointer;
                border: none;
                background: transparent;
              }
              .booking-calendar .rdp-day_button:hover:not(:disabled) {
                background: #374151;
              }
              .booking-calendar .rdp-today .rdp-day_button {
                background: #374151;
                color: #f97316;
                font-weight: 600;
              }
              .booking-calendar .rdp-selected .rdp-day_button {
                background: linear-gradient(to right, #f97316, #dc2626);
                color: white;
              }
              .booking-calendar .rdp-disabled .rdp-day_button {
                color: #4b5563;
                opacity: 0.4;
                cursor: not-allowed;
                text-decoration: line-through;
              }
              .booking-calendar .rdp-outside .rdp-day_button {
                color: #4b5563;
                opacity: 0.5;
              }
            `}</style>
            <DayPicker
              mode="single"
              selected={selectedDate}
              onSelect={handleSelect}
              disabled={isDateDisabled}
              fromDate={today}
              showOutsideDays={false}
              className="booking-calendar"
            />

            {/* Legend */}
            <div className="mt-4 pt-4 border-t border-gray-700 flex flex-wrap gap-4 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-gradient-to-r from-orange-500 to-red-600" />
                <span className="text-gray-400">Selected</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-gray-800 border border-orange-500" />
                <span className="text-gray-400">Today</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-gray-700 opacity-25" />
                <span className="text-gray-400">Unavailable</span>
              </div>
            </div>

            {unavailableDates.length > 0 && (
              <p className="mt-3 text-xs text-yellow-500 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {unavailableDates.length} date(s) already booked
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
